// 1. React
import React, { useState } from 'react';

// 2. Third-party
import { Modal, Upload, Button, Alert, Space, Typography, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

// 3. Services
import { globalFormService } from '@iso/services';

const SUCCESS_CODE = '0000';
const { Text } = Typography;

const ImportFormsModal = ({ visible, onCancel, onImported }) => {
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileList, setFileList] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setParsed(null);
    setFileName('');
    setFileList([]);
    setParseError(null);
    setLoading(false);
  };

  const clearFile = () => {
    setFileList([]);
    setFileName('');
    setParsed(null);
    setParseError(null);
  };

  // Parse + validate client-side; return false to stop antd auto-upload.
  const handleFile = (file) => {
    setFileName(file.name);
    // Show the chosen file as a named chip (controlled list).
    setFileList([{ uid: file.uid || String(Date.now()), name: file.name, status: 'done' }]);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const rows = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.forms) ? data.forms : null);
        if (!rows || rows.length === 0) {
          setParsed(null);
          setParseError('Expected a non-empty JSON array of forms.');
          return;
        }
        setParseError(null);
        setParsed(rows);
        setFileName(file.name);
      } catch (err) {
        setParsed(null);
        setParseError(`Invalid JSON: ${err.message}`);
      }
    };
    reader.onerror = () => {
      setParsed(null);
      setParseError('Could not read the file.');
    };
    reader.readAsText(file);
    return false;
  };

  const handleImport = () => {
    if (!parsed) {
      return;
    }
    setLoading(true);
    globalFormService
      .importForms(parsed)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          message.success('Import complete');
          const summary = res.summary || {};
          reset();
          // Parent hides this modal and shows the result modal.
          if (onImported) {
            onImported(summary);
          }
        } else {
          message.error((res && res.message) || 'Import failed');
        }
      })
      .catch((error) => {
        console.error('Import error:', error);
        message.error('Import failed. Please check the file format and try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal
      title="Import Forms (JSON)"
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel} className="cancel-button">
          Close
        </Button>,
        <Button
          key="import"
          type="primary"
          className="btn-success"
          onClick={handleImport}
          loading={loading}
          disabled={!parsed}
        >
          Import
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="Each form's Category must match an existing category name. Unrecognised category names are imported under “Other”; import never creates new categories."
        />

        <Upload
          accept=".json"
          beforeUpload={handleFile}
          maxCount={1}
          fileList={fileList}
          onRemove={() => {
            clearFile();
            return true;
          }}
        >
          <Button icon={<UploadOutlined />}>Select JSON file</Button>
        </Upload>

        {parseError && <Alert type="error" showIcon message={parseError} />}

        {parsed && !parseError && (
          <Alert
            type="info"
            showIcon
            message={<Text>{`${parsed.length} form(s) ready to import`}</Text>}
          />
        )}
      </Space>
    </Modal>
  );
};

export default ImportFormsModal;
